import { useCallback, useEffect, useState } from 'react';
import { appCredentialsApi, authApi } from '@/services/api';
import { Button } from '../ui/Button';
import { useToast } from '../ui/useToast';
import { translate as t, useI18n } from '@/i18n';
import { getLanguage } from '@/i18n';

type Credential = {
  id: string;
  name: string;
  scope: 'read' | 'read-write';
  createdAt: number;
  expiresAt: number;
  revokedAt: number | null;
};

export function WebDavCredentials() {
  useI18n();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'read' | 'read-write'>('read');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [createdPassword, setCreatedPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const load = useCallback(async () => {
    try {
      const result = await appCredentialsApi.list();
      if (result.data.success && result.data.data) setCredentials(result.data.data);
    } catch {
      toast({ title: t("加载 WebDAV 应用凭据失败"), variant: 'destructive' });
    }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  async function create() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const verification = await authApi.reauth({ operation: 'webdav:create', password, code });
      const challengeToken = verification.data.data?.challengeToken;
      if (!verification.data.success || !challengeToken) throw new Error(t("身份验证失败"));
      setPassword('');
      setCode('');
      const response = await appCredentialsApi.create({ name: name.trim(), scope, challengeToken });
      if (response.data.success && response.data.data) {
        setCreatedPassword(response.data.data.password ?? '');
        setName('');
        await load();
      }
    } catch {
      toast({ title: t("创建失败，请检查密码或当前两步验证码"), variant: 'destructive' });
    } finally { setBusy(false); }
  }
  async function revoke(id: string) {
    setBusy(true);
    try { await appCredentialsApi.revoke(id); await load(); }
    catch { toast({ title: t("撤销失败"), variant: 'destructive' }); }
    finally { setBusy(false); }
  }

  return <div className="space-y-4">
    <div>
      <h3 className="font-semibold">{t("WebDAV 应用凭据")}</h3>
      <p className="text-sm text-gray-500 mt-1">{t("开启两步验证后使用独立应用密码挂载 WebDAV；用户名仍为账户邮箱。默认只读、不过期，撤销后立即失效。")}</p>
    </div>
    <div className="space-y-2">
      {credentials.map((credential) => <div key={credential.id} className="flex items-center justify-between gap-3 border rounded-lg p-3">
        <div className="min-w-0">
          <p className="font-medium truncate">{credential.name}</p>
          <p className="text-xs text-gray-500">{credential.scope === 'read' ? t("只读") : t("读写")} · {credential.revokedAt ? t("已撤销") : credential.expiresAt === 0 ? t("不过期") : credential.expiresAt <= Date.now() ? t("已到期") : t("到期：{{p0}}", { p0: new Date(credential.expiresAt).toLocaleString(getLanguage()) })}</p>
        </div>
        {!credential.revokedAt && <Button variant="outline" disabled={busy} onClick={() => revoke(credential.id)}>{t("撤销")}</Button>}
      </div>)}
    </div>
    {createdPassword ? <div className="border border-amber-300 rounded-lg p-4 space-y-3">
      <p className="text-sm">{t("应用密码仅显示一次，请保存到 WebDAV 客户端。")}</p>
      <input aria-label={t("新建 WebDAV 应用密码")} readOnly value={createdPassword} className="w-full border rounded px-2 py-2 text-xs font-mono" />
      <Button onClick={() => setCreatedPassword('')}>{t("已保存，关闭显示")}</Button>
    </div> : <div className="space-y-3">
      <input aria-label={t("WebDAV 凭据名称")} maxLength={100} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("应用名称，例如 Windows 挂载")} className="w-full px-3 py-2 border rounded-lg bg-transparent" />
      <select aria-label={t("WebDAV 权限")} value={scope} onChange={(e) => setScope(e.target.value as 'read' | 'read-write')} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800">
        <option value="read">{t("只读")}</option><option value="read-write">{t("读写")}</option>
      </select>
      <p className="text-xs text-gray-500">{t("创建前再次验证：未开启两步验证时输入密码；已开启时输入当前验证码或恢复码。")}</p>
      <input aria-label={t("再次验证账户密码")} autoComplete="current-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("账户密码（未开启两步验证）")} className="w-full px-3 py-2 border rounded-lg bg-transparent" />
      <input aria-label={t("再次验证两步验证码或恢复码")} autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("两步验证码或恢复码（已开启）")} className="w-full px-3 py-2 border rounded-lg bg-transparent" />
      <Button disabled={busy || !name.trim()} onClick={create}>{busy ? t("处理中...") : t("创建应用凭据")}</Button>
    </div>}
  </div>;
}
